import _ from "lodash";
import qahoy from "views_shared/app/javascript/scripts/qahoy";
import qapi from "views_shared/app/javascript/api";
import { throwOnApiError } from "views_shared/app/javascript/api/api";

// Expert header update hook — the sibling of use_pr_profile_update_api: instantiated by the edit-modal
// body from the provider context's setters, exposes updateExpert(). It holds no state of its own.
//
// An Expert header save can span TWO records: first/last name live on the self-representing promoter
// profile (renaming there keeps the account user, profile and expert in sync server-side); description
// and headline_custom live on the expert entity. Up to two sequential PATCHes:
//
//   1. /id/api/user_profiles/:profileId/change_name_and_slug   { first_name, last_name }
//   2. /id/api/experts/:expertId                               { name?, description?, headline_custom? }
//
// The second is atomic server-side, but the pair is not: the rename can commit and the entity PATCH
// then fail. Any failure surfaces on generalError (modal-local alert, modal stays open) — never
// asyncError, which would tear the page down — followed by a nonfatal reconcile reload to re-sync
// page state from whatever the server actually committed.
const useExpertUpdateApi = ({ expertId, setIsUpdating, setGeneralError, setOpenEditModal, silentReload }) => {
  // Resolves { saved, profileCommitted }. profileCommitted survives a failed save so the caller can
  // advance its name baseline past a rename that DID commit before the entity PATCH failed — otherwise
  // a retry of the remaining fields would diff the name as still-changed and resend it.
  const updateExpert = async (
    { profileId = null, profileAttributes = {}, entityAttributes = {} } = {},
    options = {},
  ) => {
    const { closeModal = true } = options;
    let profileCommitted = false;

    try {
      setIsUpdating(true);
      setGeneralError(null);

      qahoy.track("Experts#updateExpert");

      // The rename goes first, mirroring the order the PR page's two-endpoint save uses. It is also the
      // write that resets the expert's slug, so the entity PATCH's response body (which re-selects the
      // experts view row) already reflects the new name when both run.
      if (profileId && !_.isEmpty(profileAttributes)) {
        // prProfiles.updateById routes first_name/last_name to change_name_and_slug and fires nothing
        // else when that is all it is given; it applies throwOnApiError internally.
        await qapi.prProfiles.updateById(profileId, profileAttributes);
        profileCommitted = true;
      }

      if (!_.isEmpty(entityAttributes)) {
        throwOnApiError(await qapi.experts.updateById(expertId, entityAttributes));
      }

      if (closeModal) setOpenEditModal(null);

      // The refresh doubles as the read of the post-save expert row, so the slug follow below costs no
      // extra request. It is deliberately the row the server actually has, not the values submitted:
      // both endpoints reset the slug from the new name, and collisions can make it differ from a
      // client-side guess.
      const refreshed = await silentReload();

      // Old slugs stay resolvable (friendly_id keeps slug history), so this is a soft canonicalization
      // rather than a correctness requirement — hence no reload, and no failure if it cannot happen.
      // It is scoped to the /experts/ URL because the same page is also served at /profiles/:id for the
      // owner and for admins, and that URL must survive a rename untouched.
      // V1-COMPAT: skip on V1 — its equivalent page is the id-based /sources/:id (see expertUrl in
      // views_shared/utils/urls), which a name/slug change never affects, so there is no address-bar
      // path to canonicalize into.
      if (!globalThis.Qwoted?.V1_HOSTED && refreshed?.slug && window.location.pathname.startsWith("/experts/")) {
        window.history.replaceState(null, "", `/experts/${refreshed.slug}`);
      }
      if (refreshed?.name) {
        document.title = `${refreshed.name} - Qwoted`;
      }
      return { saved: true, profileCommitted };
    } catch (error) {
      setGeneralError(error);
      try {
        await silentReload({ nonfatal: true });
      } catch {
        // A rejecting reconcile reload must not escalate the already-surfaced save error into an
        // unhandled rejection out of the submit handler.
      }
      return { saved: false, profileCommitted };
    } finally {
      setIsUpdating(false);
    }
  };

  return { updateExpert };
};

export default useExpertUpdateApi;
